import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import Downshift from 'downshift'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import Portal from 'components/Portal/Portal'

const MultiComplete = ({
  value = [],
  options = [],
  width,
  label,
  placeholder,
  required,
  maxRows,
  onChange,
  disabled,
  ariaLabel,
  ...props
}) => {
  const isMobile = useSelector((state) => state.app.isMobile)
  const inputRef = useRef(null)
  const menuRef = useRef(null)
  const [updateMenuOnNextValueChange, setUpdateMenuOnNextValueChange] = useState(false)

  const changeSelection = (selected) => {
    const exists = value.includes(selected.value)
    const newValue = exists ? value.filter((v) => v !== selected.value) : [...value, selected.value]
    onChange(newValue)
  }

  const remove = (i) => {
    changeSelection(options.find((o) => o.value == value[i]))
  }

  const handleEnterKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const stateReducer = (dstate, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.clickItem:
        if (props.leaveOpenOnSelect) {
          setUpdateMenuOnNextValueChange(true)
          return {
            ...changes,
            isOpen: true,
          }
        }
        return changes
      default:
        return changes
    }
  }

  useEffect(() => {
    if (updateMenuOnNextValueChange && menuRef.current && menuRef.current.updateMenuPosition) {
      setUpdateMenuOnNextValueChange(false)
      menuRef.current.updateMenuPosition()
    }
  }, [value.length])

  return (
    <div>
      <Downshift onSelect={changeSelection} stateReducer={stateReducer} itemToString={() => ''}>
        {({
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          getToggleButtonProps,
          isOpen,
          inputValue,
          highlightedIndex,
        }) => (
          <div className="multi-complete">
            <label className="field-label" {...getLabelProps()}>
              {label}
              {required && label !== '' && (
                <span className="required" aria-hidden={true}>
                  *
                </span>
              )}
            </label>

            {value.length > 0 && (
              <div className="tags-wrapper">
                {value.map((v, i) => (
                  <Tag key={`${v}${i}`} onClick={() => remove(i)} disabled={disabled}>
                    {(options.find((o) => o.value === v) || {}).label || v}
                  </Tag>
                ))}
              </div>
            )}

            {(!maxRows || value.length < maxRows) && (
              <div className="multi-input-wrapper" style={{ width }}>
                <input
                  type="text"
                  {...getInputProps({
                    ref: inputRef,
                    placeholder,
                    onKeyDown: handleEnterKey,
                    disabled,
                  })}
                  aria-label={ariaLabel}
                />
                <button
                  {...getToggleButtonProps({
                    className: 'menu-button ' + (isOpen ? 'open' : ''),
                    disabled,
                  })}
                >
                  <CaretDownIcon />
                </button>
              </div>
            )}

            <Portal isOpened>
              <Menu
                ref={menuRef}
                isOpen={isOpen}
                getMenuProps={getMenuProps}
                getItemProps={getItemProps}
                inputValue={inputValue}
                highlightedIndex={highlightedIndex}
                options={options.filter((o) => !value.includes(o.value))}
                isMobile={isMobile}
                getInputRef={() => inputRef.current}
              />
            </Portal>
          </div>
        )}
      </Downshift>
    </div>
  )
}

const Menu = React.forwardRef(
  (
    {
      isOpen,
      getMenuProps,
      getItemProps,
      inputValue,
      options,
      highlightedIndex,
      getInputRef,
      isMobile,
    },
    ref
  ) => {
    const [anchor, setAnchor] = useState(null)

    useEffect(() => {
      if (isOpen) {
        const scrollWrapper = isMobile
          ? window
          : document.getElementById('wizard-body-scroll-container')
        const updateMenuPosition = () => setAnchor(getInputRef().getBoundingClientRect())
        updateMenuPosition()
        scrollWrapper && scrollWrapper.addEventListener('scroll', updateMenuPosition)
        return () =>
          scrollWrapper && scrollWrapper.removeEventListener('scroll', updateMenuPosition)
      }
    }, [isOpen, getInputRef, isMobile])

    const calculateHeight = () =>
      anchor && anchor.bottom + 240 > window.innerHeight - 50
        ? 240 - (anchor.bottom + 240 - window.innerHeight) - 50
        : 240

    return (
      isOpen &&
      anchor && (
        <ul
          {...getMenuProps({
            className: 'multi-complete-menu',
            style: {
              left: anchor.left + 3,
              top: anchor.bottom - 4,
              minWidth: anchor.width - 6,
              maxHeight: calculateHeight(),
            },
            ref: ref,
          })}
        >
          {options
            .filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
            .map((opt, i) => (
              <li
                aria-label={`option - ${opt.label}`}
                {...getItemProps({
                  item: opt,
                  key: opt.value,
                  className: highlightedIndex === i ? 'highlighted' : '',
                })}
              >
                {opt.label}
              </li>
            ))}
        </ul>
      )
    )
  }
)

const Tag = ({ children, onClick, disabled }) => (
  <div className="multi-tag">
    <button
      className="remove-button"
      onClick={onClick}
      aria-label={`Unselect ${children.toString()}`}
      disabled={disabled}
    >
      ✕
    </button>
    {children}
  </div>
)

export default MultiComplete
